<template>
  <div>
    <div
      class="column agent-license-filter--box-container"
      :class="{ 'agent-license-filter--box-container-closed': !helper }"
    >
      <div class="agents-table-filter-checkbox-container">
        <incredi-check-box
          v-model:value="allCheckbox"
          label="All"
          data-role="all-checkbox"
          :data-testid="elementIds.agents.filtersMenu.licenseType.all"
        />
        <div class="q-ml-md">
          <div
            v-if="hasInitiatorLicense"
            class="column"
          >
            <incredi-check-box
              v-model:value="allInitiators"
              label="Only Initiator"
              data-role="allInitiators-checkbox"
              :data-testid="elementIds.agents.filtersMenu.licenseType.initiators"
            />
            <incredi-check-box
              v-if="hasBothInitiatorTypesLicense"
              v-model:value="initiator"
              label="Initiator"
              data-role="initiator-checkbox"
              class="q-ml-md"
            />
            <incredi-check-box
              v-if="hasBothInitiatorTypesLicense"
              v-model:value="ciInitiator"
              label="CI Initiator"
              data-role="ciinitiator-checkbox"
              class="q-ml-md"
            />
          </div>
        </div>
        <div class="q-ml-md">
          <div
            v-if="visibility.helper !== false"
            class="column"
          >
            <incredi-check-box
              v-model:value="allHelpers"
              label="Only Helper"
              data-role="allHelpers-checkbox"
            />
            <incredi-check-box
              v-if="visibility.cloudAgent !== false"
              v-model:value="helper"
              label="On Prem"
              data-role="helper-checkbox"
              class="q-ml-md"
            />
            <incredi-check-box
              v-if="visibility.cloudAgent !== false"
              v-model:value="cloudAgent"
              label="Cloud"
              data-role="cloud-checkbox"
              class="q-ml-md"
            />
          </div>
        </div>
        <div class="q-ml-md">
          <div
            v-if="hasInitiatorLicense"
            class="column"
          >
            <incredi-check-box
              v-model:value="allHelperAndAllTypesInitiators"
              label="Both Initiator & Helper"
              data-role="allHelpersAndInitiators-checkbox"
            />
            <incredi-check-box
              v-if="hasBothInitiatorTypesLicense"
              v-model:value="bothHelperAndInitiator"
              label="Initiator & Helper"
              data-role="both-helper-and-initiator-checkbox"
              class="q-ml-md"
            />
            <incredi-check-box
              v-if="hasBothInitiatorTypesLicense"
              v-model:value="bothHelperAndCIInitiator"
              label="CI Initiator & Helper"
              data-role="both-helper-and-ciinitiator-checkbox"
              class="q-ml-md"
            />
          </div>
        </div>
        <incredi-check-box
          v-model:value="local"
          label="None"
          data-role="local-checkbox"
          class="q-ml-md"
        />
      </div>
    </div>
    <div
      v-if="visibility.fixedFloating"
      class="agent-license-filter--select-input"
    >
      <div
        class="ibf-button2 brand_almost_black"
        style="margin-bottom: 8px"
      >
        Additional options:
      </div>
      <incredi-dropdown-menu
        v-if="visibility.fixedFloating"
        v-model:value="initiatorHelperTypeSelectedOption"
        :data-testid="elementIds.agents.filtersMenu.licenseType.fixedFloating"
        layer-order="true"
        :options="['All', 'Fixed', 'Floating']"
        label="Fixed/Floating"
      />
      <incredi-dropdown-menu
        v-if="visibility.withBuildCache"
        v-model:value="initiatorTypeWithBuildCache"
        :data-testid="elementIds.agents.filtersMenu.licenseType.withBuildCache"
        layer-order="true"
        class="incredi-drop-down"
        :disabled="!enabledFilters.withBuildCache"
        :options="[
          $t('agentsTable.filters.license.AdditionalOptions.BuildCache.all'),
          $t('agentsTable.filters.license.AdditionalOptions.BuildCache.withBuildCache'),
          $t('agentsTable.filters.license.AdditionalOptions.BuildCache.withOutBuildCache'),
        ]"
        label="Build cache (only for initiators)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { defineComponent, inject } from 'vue';
import { IKAgentsDrawerFilter } from '@/types/injectionKeys';
import { useCommonDataQuery } from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { IKAgentsSelected } from '@/views/agents/components/agents-table/agents-table.InjectionKeys';
import elementIds from '@/const/elementIds.json';
import { useMatomo } from '@/services/matomo/matomo.service';
import IncrediCheckBox from '../../../../../../../inputs/incrediCheckBox.vue';
import incrediDropdownMenu from '../../../../../../../inputs/incrediDropdownMenu.vue';

export default defineComponent({
  components: { incrediDropdownMenu, IncrediCheckBox },
  props: {
    visibility: {
      type: Object,
      default: () => ({
        cloudAgent: true,
        helper: true,
        initiator: true,
        ciInitiator: true,
        withBuildCache: true,
        fixedFloating: true,
      }),
    },
    enabledFilters: {
      type: Object,
      default: () => ({
        cloudAgent: true,
        helper: true,
        initiator: true,
        ciInitiator: true,
        withBuildCache: true,
        fixedFloating: true,
      }),
    },
  },
  setup() {
    const matomo = useMatomo();
    const agentsFilter = inject(IKAgentsDrawerFilter);
    const coordinatorsService = inject(IKCoordinatorsService);
    const selectedAgents = inject(IKAgentsSelected);
    const GQL_commonData = useCommonDataQuery(() => ({ coordinatorId: coordinatorsService.selectedCoordinator.value || '' }));
    return { agentsFilter, GQL_commonData, selectedAgents, elementIds, matomo };
  },
  computed: {
    ...mapGetters({}),
    cloudStatus() {
      return this.GQL_commonData?.result?.value?.ibManagerEntities?.settings?.common?.cloud?.cloudStatus;
    },
    filterState() {
      return this.agentsFilter?.filter.value;
    },
    hasInitiatorLicense() {
      return !!this.visibility.initiator || !!this.visibility.ciInitiator;
    },
    hasBothInitiatorTypesLicense() {
      return !!this.visibility.initiator && !!this.visibility.ciInitiator;
    },
    allCheckbox: {
      get() {
        const checboxes = [this.local, this.initiator, this.ciInitiator, this.helper, this.bothHelperAndInitiator, this.bothHelperAndCIInitiator];
        if (this.visibility.cloudAgent) checboxes.push(this.cloudAgent);
        if (checboxes.every(checkbox => checkbox) && !this.isAdditionalOptionsDropDownChanged) {
          this.agentsFilter?.clearGQL_AgentsFilter(['licenseFilter']);
          return true;
        }
        if (checboxes.some(checkbox => checkbox)) return null;
        return false;
      },
      set(val) {
        this.local = val;
        this.initiator = val;
        this.ciInitiator = val;
        this.helper = val;
        this.cloudAgent = val;
        this.bothHelperAndInitiator = val;
        this.bothHelperAndCIInitiator = val;
      },
    },
    isAdditionalOptionsDropDownChanged() {
      const changeCondition = 'All';
      return this.initiatorTypeWithBuildCache !== changeCondition || this.initiatorHelperTypeSelectedOption !== changeCondition;
    },
    allHelpers: {
      get() {
        const allHelpers = [this.helper];
        if (this.visibility.cloudAgent) allHelpers.push(this.cloudAgent);
        if (allHelpers.every(e => e)) return true;
        if (allHelpers.some(e => e)) return null;
        return false;
      },
      set(val) {
        this.helper = val;
        this.cloudAgent = val;
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'Only Helper',
          value: val ? 1 : 0,
        });
      },
    },
    allHelperAndAllTypesInitiators: {
      get() {
        const allHelpers = [this.bothHelperAndInitiator, this.bothHelperAndCIInitiator];
        if (allHelpers.every(e => e)) return true;
        if (allHelpers.some(e => e)) return null;
        return false;
      },
      set(val) {
        this.bothHelperAndInitiator = val;
        this.bothHelperAndCIInitiator = val;
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'Both Initiator & Helper',
          value: val ? 1 : 0,
        });
      },
    },
    allInitiators: {
      get() {
        const allInitiators = [this.initiator];
        if (this.visibility.ciInitiator) allInitiators.push(this.ciInitiator);
        if (allInitiators.every(e => e)) return true;
        if (allInitiators.some(e => e)) return null;
        return false;
      },
      set(val) {
        this.initiator = val;
        this.ciInitiator = val;
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'Only Initiator',
          value: val ? 1 : 0,
        });
      },
    },
    local: {
      get() {
        return this.filterState.licensesFilter.local;
      },
      set(value) {
        this?.agentsFilter?.updateFilter({
          filter: 'local',
          group: 'licensesFilter',
          value,
        });
        this.agentsFilter?.setGQL_AgentsFilter('licenseFilter');
        this.selectedAgents?.clearSelection();
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'No License',
          value: value ? 1 : 0,
        });
      },
    },
    bothHelperAndInitiator: {
      get() {
        return this.filterState.licensesFilter.bothHelperAndInitiator;
      },
      set(value) {
        this.agentsFilter?.updateFilter({
          filter: 'bothHelperAndInitiator',
          group: 'licensesFilter',
          value,
        });
        this.agentsFilter?.setGQL_AgentsFilter('licenseFilter');
        this.selectedAgents?.clearSelection();
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'Initiator & Helper',
          value: value ? 1 : 0,
        });
      },
    },
    bothHelperAndCIInitiator: {
      get() {
        return this.filterState.licensesFilter.bothHelperAndCIInitiator;
      },
      set(value) {
        this.agentsFilter?.updateFilter({
          filter: 'bothHelperAndCIInitiator',
          group: 'licensesFilter',
          value,
        });
        this.agentsFilter?.setGQL_AgentsFilter('licenseFilter');
        this.selectedAgents?.clearSelection();
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'CI Initiator & Helper',
          value: value ? 1 : 0,
        });
      },
    },
    initiator: {
      get() {
        return this.filterState.licensesFilter.initiator;
      },
      set(value) {
        this?.agentsFilter?.updateFilter({
          filter: 'initiator',
          group: 'licensesFilter',
          value,
        });
        this.agentsFilter?.setGQL_AgentsFilter('licenseFilter');
        this.selectedAgents?.clearSelection();
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'Initiator',
          value: value ? 1 : 0,
        });
      },
    },
    ciInitiator: {
      get() {
        return this.filterState.licensesFilter.ciInitiator;
      },
      set(value) {
        this?.agentsFilter?.updateFilter({
          filter: 'ciInitiator',
          group: 'licensesFilter',
          value,
        });
        this.agentsFilter?.setGQL_AgentsFilter('licenseFilter');
        this.selectedAgents?.clearSelection();
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'CI Initiator',
          value: value ? 1 : 0,
        });
      },
    },
    helper: {
      get() {
        return this.filterState.licensesFilter.helper;
      },
      set(value) {
        this?.agentsFilter?.updateFilter({
          filter: 'helper',
          group: 'licensesFilter',
          value,
        });
        this.agentsFilter?.setGQL_AgentsFilter('licenseFilter');
        this.selectedAgents?.clearSelection();
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'On Prem',
          value: value ? 1 : 0,
        });
      },
    },
    cloudAgent: {
      get() {
        return this.filterState.licensesFilter.cloudAgent || false;
      },
      set(value) {
        if (this.visibility.cloudAgent) {
          this?.agentsFilter?.updateFilter({
            filter: 'cloudAgent',
            group: 'licensesFilter',
            value,
          });
          this.agentsFilter?.setGQL_AgentsFilter('licenseFilter');
          this.selectedAgents?.clearSelection();
          this.matomo?.trackEvent({
            category: 'Agents Filter',
            action: 'Select / Unselect License Type',
            name: 'Cloud',
            value: value ? 1 : 0,
          });
        }
      },
    },
    initiatorHelperTypeSelectedOption: {
      get() {
        return this.filterState.licensesFilter.initiatorType.value && this.filterState.licensesFilter.helperType && this.filterState.licensesFilter.ciInitiatorType.value;
      },
      set(value) {
        this?.agentsFilter?.updateFilter({
          filter: 'initiatorType',
          group: 'licensesFilter',
          value: {
            value,
            withBuildCache: this.filterState.licensesFilter.initiatorType.withBuildCache,
          },
        });
        this?.agentsFilter?.updateFilter({
          filter: 'ciInitiatorType',
          group: 'licensesFilter',
          value: {
            value,
            withBuildCache: this.filterState.licensesFilter.ciInitiatorType.withBuildCache,
          },
        });
        this?.agentsFilter?.updateFilter({
          filter: 'helperType',
          group: 'licensesFilter',
          value,
        });
        this.agentsFilter?.setGQL_AgentsFilter('licenseFilter');
        this.selectedAgents?.clearSelection();
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'Fixed/Floating',
          value: value ? 1 : 0,
        });
      },
    },

    initiatorTypeWithBuildCache: {
      get() {
        return this.filterState.licensesFilter.initiatorType.withBuildCache && this.filterState.licensesFilter.ciInitiatorType.withBuildCache;
      },
      set(value) {
        this?.agentsFilter?.updateFilter({
          filter: 'initiatorType',
          group: 'licensesFilter',
          value: {
            value: this.filterState.licensesFilter.initiatorType.value,
            withBuildCache: value,
          },
        });
        this?.agentsFilter?.updateFilter({
          filter: 'ciInitiatorType',
          group: 'licensesFilter',
          value: {
            value: this.filterState.licensesFilter.ciInitiatorType.value,
            withBuildCache: value,
          },
        });

        this.agentsFilter?.setGQL_AgentsFilter('licenseFilter');
        this.selectedAgents?.clearSelection();
        this.matomo?.trackEvent({
          category: 'Agents Filter',
          action: 'Select / Unselect License Type',
          name: 'Build cache (only for initiators)',
          value: value ? 1 : 0,
        });
      },
    },
  },
  watch: {
    cloudStatus: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue === 'Disabled') {
          this?.agentsFilter?.updateFilter({
            filter: 'cloudAgent',
            group: 'licensesFilter',
            value: undefined,
          });
        }
        // the use of the old value here is to make sure this watch only happens the moment the user unlocks the cloud and the status goes from disabled to enabled.
        if (newValue === 'Enabled' && oldValue === 'Disabled') {
          this?.agentsFilter?.updateFilter({
            filter: 'cloudAgent',
            group: 'licensesFilter',
            value: true,
          });
        }
      },
    },
  },
});
</script>

<style lang="scss">
@import '../../../../agents-table-filters-style';

.license-filter-content {
  margin-top: 0px !important;

  .incredi-checkbox:first-of-type {
    margin-top: 0px !important;
  }
}

.agent-license-filter--box-container-closed {
  margin: 0 !important;
}

.agent-license-filter--select-input {
  margin-left: 18px;
  margin-top: 24px;
  margin-bottom: 16px;

  .incredi-drop-down {
    margin-top: 16px;
  }

  .menu-selected-option {
    font-size: 12px;
    color: $-text--dark;
  }
}

.agent-license-filter--box-container {
  .agent-license-filter--select-input {
    margin-left: 27px;
    margin-right: 12px;
    margin-top: 8px;
  }
}
</style>
